import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, IconButton,
  CircularProgress, Tooltip,
} from '@mui/material';
import {
  LocalHospital as Hospital,
  MedicalServices as Stethoscope,
  Science as FlaskConical,
  Business as Building2,
  LocalPharmacy as Pill,
  ChevronRight,
  Search as SearchIcon,
  Clear as ClearIcon,
  Apps as AppsIcon,
  ShoppingCart as ShoppingCartIcon,
  Refresh as RefreshIcon,
  CloudOff as CloudOffIcon,
  Person as PersonIcon,
  ChevronLeft as ChevronLeftIcon,
  LockReset as LockResetIcon,
  ExitToApp as SignOutIcon,
} from '@mui/icons-material';

import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

import { getAllDepartments, categorie } from '../data/department';
import { getAcc } from '../BaseURL';
import ConsumableRequisitionDialog from '../Procurement/ConsumableRequisitionDialog';
import ChangePasswordDialog from '../ChangePasswordDialog';
import { logoutUser } from '../services/socket';
import { BRAND, PRODUCT, FONT_STACK, GRADIENT_NAVY } from '../../brand';
import useInstallProfile from '../useInstallProfile';

// ═════════════════════════════════════════════════════════════════════════════
//  DASHBOARD — department workspace
// ═════════════════════════════════════════════════════════════════════════════
//
//  A side rail of categories beside a working area that shows one group at a
//  time. The previous version stacked every category vertically, so a facility
//  with 35 departments meant a long scroll before reaching Administration.
//  The rail turns that into one click and doubles as the count summary.
//
//  Two things carried over unchanged because they are load-bearing:
//    · access control — a non-administrator only ever sees the departments
//      returned by /getAcc for their role
//    · the department → route map in onDepartmentSelect
//
//  Note on layout: MUI 7's <Grid> ignores `item`/`xs`/`md`, which is why the
//  old cards had ragged widths. Everything here is plain CSS Grid.

const ALL = '__all__';

// Rail geometry.
//
// TOP_OFFSET is the app-bar height MainLayout reserves with its ToolbarSpacer
// (the AppBar is position:fixed), so the rail starts directly beneath it.
//
// IMPORTANT — <main> has NO padding of its own. `.main_side` in index.css
// sets `padding: 0% !important`, overriding the theme.spacing(3) that
// MainLayout's styled('main') asks for. So this page is responsible for its
// own gutters, and the content column's left margin is the FULL rail width,
// not the rail width minus a padding that does not exist. Getting that wrong
// slid the content under the rail and clipped the headings.
const RAIL_WIDTH     = 268;
const RAIL_COLLAPSED = 76;
const TOP_OFFSET     = 64;
const RAIL_PREF_KEY  = 'dashboardRailCollapsed';

// Category accents. Clinical takes the brand green; the rest are distinct
// enough to tell apart at a glance without turning the page into confetti.
const CATEGORY_STYLE = [
  { match: 'Clinical',       icon: Stethoscope,  accent: BRAND.green },
  { match: 'Diagnostic',     icon: FlaskConical, accent: '#2B7FC4' },
  { match: 'Administrative', icon: Building2,    accent: '#E08B2F' },
  { match: 'Specialty',      icon: Pill,         accent: '#7C5CBF' },
];

function styleFor(category = '') {
  return (
    CATEGORY_STYLE.find((c) => category.includes(c.match)) ||
    { icon: Stethoscope, accent: BRAND.navy }
  );
}

function greetingFor(date) {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ─── Rail row ────────────────────────────────────────────────────────────────
function RailRow({ icon: Icon, label, count, accent, active, collapsed, onClick }) {
  const row = (
    <Box
      component="button" type="button" onClick={onClick}
      aria-label={collapsed ? `${label} (${count})` : undefined}
      sx={{
        display: 'flex', alignItems: 'center', width: '100%',
        gap: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? .5 : 1.25, py: 1.05,
        borderRadius: '10px', cursor: 'pointer',
        font: 'inherit', textAlign: 'left', position: 'relative',
        background: active ? 'rgba(255,255,255,.13)' : 'transparent',
        border: '1px solid', borderColor: active ? 'rgba(255,255,255,.22)' : 'transparent',
        transition: 'background .16s ease, border-color .16s ease',
        '&:hover': { background: 'rgba(255,255,255,.09)' },
        '&:focus-visible': { outline: `2px solid ${BRAND.greenLight}`, outlineOffset: 2 },
      }}
    >
      {/* active marker doubles as the category's colour key */}
      {!collapsed && (
        <Box
          sx={{
            width: 3, height: 20, borderRadius: 2, flexShrink: 0,
            background: active ? (accent || BRAND.greenLight) : 'transparent',
          }}
        />
      )}

      <Box sx={{ position: 'relative', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Icon sx={{ fontSize: 18, color: active ? '#fff' : 'rgba(255,255,255,.55)' }} />
        {/* Collapsed, the count has nowhere to sit inline, so it becomes a
            small badge — the number is the whole point of the row. */}
        {collapsed && (
          <Box
            sx={{
              position: 'absolute', top: -7, right: -11,
              minWidth: 16, height: 16, px: .375, borderRadius: '9px',
              display: 'grid', placeItems: 'center',
              background: active ? (accent || BRAND.greenLight) : 'rgba(255,255,255,.16)',
            }}
          >
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
              {count}
            </Typography>
          </Box>
        )}
      </Box>

      {!collapsed && (
        <>
          <Typography
            sx={{
              flex: 1, minWidth: 0, fontSize: 12.5,
              fontWeight: active ? 700 : 500,
              color: active ? '#fff' : 'rgba(255,255,255,.7)',
            }}
            noWrap
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: 11, fontWeight: 700, flexShrink: 0,
              px: .875, py: .125, borderRadius: '20px',
              background: active ? 'rgba(255,255,255,.18)' : 'rgba(255,255,255,.07)',
              color: active ? '#fff' : 'rgba(255,255,255,.5)',
            }}
          >
            {count}
          </Typography>
        </>
      )}
    </Box>
  );

  return collapsed
    ? <Tooltip title={`${label} · ${count}`} placement="right" arrow>{row}</Tooltip>
    : row;
}

// ─── Stat tile ───────────────────────────────────────────────────────────────
function StatTile({ label, value, accent, small }) {
  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2 }, py: { xs: 1.25, sm: 1.5 },
        borderRadius: '13px', minWidth: 0,
        background: '#fff', border: `1px solid ${BRAND.line}`,
      }}
    >
      <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: BRAND.inkFaint, letterSpacing: '1px', textTransform: 'uppercase' }} noWrap>
        {label}
      </Typography>
      {/* Numbers never wrap. Text values (a role like "System Administrator")
          are allowed two lines on narrow screens rather than being ellipsised
          to "System Admini…" — the tile exists to state the role. */}
      <Typography
        sx={{
          fontSize: small ? { xs: 12.5, sm: 14 } : { xs: 19, sm: 22 },
          fontWeight: 800, lineHeight: 1.3, mt: .25,
          color: accent || BRAND.ink,
          ...(small
            ? {
                display: '-webkit-box', WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }
            : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
const Dashboard = () => {
  // What kind of installation this is. Fetched once per tab and shared, and it
  // falls back to "facility, all departments" if the request fails — so a
  // hospital never loses its departments to a network blip.
  const install = useInstallProfile();

  const [userInfo, setUserInfo]       = useState(null);
  const [facility, setFacility]       = useState(null);
  const [getAccInfo, setgetAccInfo]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCat, setActiveCat]     = useState(ALL);
  const [departments]                 = useState(getAllDepartments);
  const [categories]                  = useState(categorie);

  // "In-charge" users (staff.is_staff = 1) get a Request Consumables action.
  // Click opens a self-contained dialog that posts a 'consumable' requisition
  // into the same Procurement inbox drugs use.
  const [consumablesDialogOpen, setConsumablesDialogOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Rail collapse. Read synchronously from localStorage on first render so the
  // rail does not flash open then snap shut for someone who keeps it closed.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(RAIL_PREF_KEY) === '1'; } catch (_) { return false; }
  });

  const toggleRail = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(RAIL_PREF_KEY, next ? '1' : '0'); } catch (_) { /* private mode */ }
      return next;
    });
  };

  const navigate = useNavigate();

  // Mirrors handleLogout in MainLayout: tell the socket server the user has
  // gone so their presence and call routing are torn down, then clear the
  // session and return to the sign-in gate.
  const handleSignOut = async () => {
    const confirmed = await Swal.fire({
      title: 'Sign out?',
      text: 'You will need your username and password to get back in.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sign out',
      cancelButtonText: 'Stay signed in',
      confirmButtonColor: BRAND.danger,
      cancelButtonColor: BRAND.inkMuted,
    });
    if (!confirmed.isConfirmed) return;

    try {
      if (userInfo?.trans_code) logoutUser(String(userInfo.trans_code));
    } catch (err) {
      console.error('socket logout failed:', err);
    }
    localStorage.removeItem('userInfo');
    navigate('/');
  };

  useEffect(() => {
    try {
      const savedUserInfo = localStorage.getItem('userInfo');
      if (savedUserInfo) {
        const parsed = JSON.parse(savedUserInfo);
        if (parsed && parsed.name && parsed.role) {
          setUserInfo(parsed);
        } else {
          throw new Error('Invalid user info structure');
        }
      } else {
        throw new Error('No user info found');
      }
    } catch (err) {
      console.error('Failed to load user info:', err);
      setError('Failed to load user information');
    }

    // Written by the sign-in screen. Purely decorative here, so a miss is fine.
    try {
      const saved = localStorage.getItem('companyInfo');
      if (saved) setFacility(JSON.parse(saved));
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (userInfo) loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const loadRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      const role = userInfo.role;
      const response = await axios.post(getAcc, { role });
      if (response.data.success && Array.isArray(response.data.results)) {
        setgetAccInfo(response.data.results);
      } else {
        throw new Error('Failed to load roles');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to load roles';
      setError(errorMessage);
      await Swal.fire({ title: 'Error', text: errorMessage, icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ── Access control — unchanged ─────────────────────────────────────────────
  const accessibleDepartmentIds = useMemo(() => {
    if (userInfo?.role === 'System Administrator') {
      return departments.map((dept) => dept.id);
    }
    if (!Array.isArray(getAccInfo)) return [];
    return getAccInfo.map((item) => item.department).filter(Boolean);
  }, [getAccInfo, userInfo?.role, departments]);

  // Two independent gates, applied in this order, and they answer different
  // questions:
  //
  //   install.departments  — does this INSTALLATION have the department at all?
  //                          A claims bureau has no wards; a district hospital
  //                          has no facility-onboarding screens. Applies to
  //                          everyone, System Administrators included, because
  //                          it is about what was purchased and configured,
  //                          not about permission.
  //
  //   accessibleDepartments — may THIS USER reach it? Per-role, and System
  //                           Administrators bypass it.
  //
  // Conflating the two would mean a permission grant could reveal a department
  // whose tables this installation never populated.
  const installedDepartments = useMemo(() => {
    if (!Array.isArray(install.departments)) return departments;   // null = all
    return departments.filter((d) => install.departments.includes(d.name));
  }, [departments, install.departments]);

  const accessibleDepartments = useMemo(() => {
    if (userInfo?.role === 'System Administrator') return installedDepartments;
    if (!accessibleDepartmentIds.length) return [];
    return accessibleDepartmentIds
      .map((deptId) => installedDepartments.find((d) => d.id === deptId))
      .filter(Boolean);
  }, [accessibleDepartmentIds, installedDepartments, userInfo?.role]);

  const filteredDepartments = useMemo(() => {
    if (!searchQuery.trim()) return accessibleDepartments;
    const q = searchQuery.toLowerCase();
    return accessibleDepartments.filter(
      (dept) =>
        dept.name.toLowerCase().includes(q) ||
        dept.description.toLowerCase().includes(q)
    );
  }, [accessibleDepartments, searchQuery]);

  const groupedDepartments = useMemo(() => {
    const grouped = {};
    if (!filteredDepartments.length) return grouped;

    Object.entries(categories).forEach(([categoryName, deptIds]) => {
      const categoryDepts = filteredDepartments.filter((dept) => deptIds.includes(dept.id));
      if (categoryDepts.length > 0) grouped[categoryName] = categoryDepts;
    });

    const uncategorised = filteredDepartments.filter(
      (dept) => !Object.values(categories).flat().includes(dept.id)
    );
    if (uncategorised.length > 0) grouped['Other Services'] = uncategorised;

    return grouped;
  }, [filteredDepartments, categories]);

  // Rail counts ignore the search box: the rail is a map of the whole
  // workspace, and having its numbers shift while typing would be disorienting.
  const railCounts = useMemo(() => {
    const out = {};
    Object.entries(categories).forEach(([name, ids]) => {
      const n = accessibleDepartments.filter((d) => ids.includes(d.id)).length;
      if (n > 0) out[name] = n;
    });
    const other = accessibleDepartments.filter(
      (d) => !Object.values(categories).flat().includes(d.id)
    ).length;
    if (other > 0) out['Other Services'] = other;
    return out;
  }, [accessibleDepartments, categories]);

  // A search that matches nothing in the selected category, but something
  // elsewhere, would look like a broken search box. So searching widens the
  // view to every category until the box is cleared.
  const searching = !!searchQuery.trim();
  const visibleGroups = useMemo(() => {
    if (searching || activeCat === ALL) return groupedDepartments;
    return groupedDepartments[activeCat]
      ? { [activeCat]: groupedDepartments[activeCat] }
      : {};
  }, [groupedDepartments, activeCat, searching]);

  const visibleCount = useMemo(
    () => Object.values(visibleGroups).reduce((n, list) => n + list.length, 0),
    [visibleGroups]
  );

  // ── Department → route. Unchanged. ─────────────────────────────────────────
  const onDepartmentSelect = (department) => {
    if (department.name === 'Medical records')          navigate('/dashboard/Record');
    if (department.name === 'Pharmacy')                 navigate('/dashboard/Pharmacy');
    if (department.name === 'Billing & Accounts')       navigate('/dashboard/Accounts');
    if (department.name === 'General Medical Services') navigate('/dashboard/Doctors');
    if (department.name === 'Wards & Nursing Services') navigate('/dashboard/Wards');
    if (department.name === 'Procurement & Supplies')   navigate('/dashboard/Procurement');
    if (department.name === 'Investigations')           navigate('/dashboard/Laboratory');
    if (department.name === 'Hospital Claims')          navigate('/dashboard/Claims');
    if (department.name === 'Health Information')       navigate('/dashboard/HealthInformation');
    if (department.name === 'Emergency (ER/A&E)')       navigate('/dashboard/Emergency');
    if (department.name === 'Human Resources (HR)')     navigate('/dashboard/HumanResources');
    if (department.name === 'IT / ICT Department')      navigate('/dashboard/ICT');
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading || !userInfo) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '70vh', fontFamily: FONT_STACK }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2.5 }}>
            <CircularProgress size={54} thickness={3.5} sx={{ color: BRAND.navy }} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
              <Hospital sx={{ fontSize: 24, color: BRAND.green }} />
            </Box>
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: BRAND.ink }}>
            Preparing your workspace
          </Typography>
          <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, mt: .5 }}>
            Loading the departments you have access to…
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '70vh', p: 3, fontFamily: FONT_STACK }}>
        <Box
          sx={{
            maxWidth: 420, width: '100%', textAlign: 'center', background: '#fff',
            borderRadius: '18px', p: { xs: 3.5, sm: 5 },
            border: `1px solid ${BRAND.line}`, boxShadow: '0 16px 48px rgba(15,31,61,.08)',
          }}
        >
          <Box sx={{ width: 62, height: 62, borderRadius: '50%', mx: 'auto', mb: 2.5, display: 'grid', placeItems: 'center', background: '#FDECEC' }}>
            <CloudOffIcon sx={{ fontSize: 30, color: BRAND.danger }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: BRAND.ink, mb: 1.25 }}>
            Could not load your workspace
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: BRAND.inkMuted, lineHeight: 1.75, mb: 3.5 }}>
            {error}
          </Typography>
          <Button
            fullWidth variant="contained" startIcon={<RefreshIcon />}
            onClick={loadRoles}
            sx={{
              py: 1.25, borderRadius: '11px', textTransform: 'none', fontWeight: 700,
              background: `linear-gradient(135deg, ${BRAND.greenLight} 0%, ${BRAND.green} 55%, ${BRAND.greenDark} 100%)`,
              boxShadow: `0 4px 14px ${BRAND.green}42`,
              '&:hover': { filter: 'brightness(1.06)' },
            }}
          >
            Try again
          </Button>
        </Box>
      </Box>
    );
  }

  const firstName = userInfo.name?.split(' ')[0] || 'there';

  // ── Rail contents, shared by the desktop column and the mobile strip ──────
  const railItems = [
    { key: ALL, label: 'All departments', icon: AppsIcon, count: accessibleDepartments.length, accent: BRAND.greenLight },
    ...Object.entries(railCounts).map(([name, count]) => ({
      key: name, label: name, count,
      icon: styleFor(name).icon, accent: styleFor(name).accent,
    })),
  ];

  const railW = collapsed ? RAIL_COLLAPSED : RAIL_WIDTH;

  return (
    <Box sx={{ fontFamily: FONT_STACK }}>
      {/* ══════════════════════════════════════════════════════════════════
          SIDE RAIL — fixed, floor to ceiling, flush to the window edge.

          `position: fixed` rather than a column in the page grid. As a grid
          child it was only as tall as its own content, so it ended partway
          down and left a dead gutter beneath it — the "hanging" look. Fixed
          means it always spans from under the app bar to the bottom of the
          window, and the category list stays put while the cards scroll.

          It is pinned to left: 0, which deliberately escapes the 24px padding
          MainLayout puts on <main>; the content column re-adds that offset
          below. TOP_OFFSET is the app-bar height MainLayout reserves with its
          ToolbarSpacer.
         ══════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
          position: 'fixed', top: TOP_OFFSET, left: 0, bottom: 0,
          width: railW, zIndex: 1100,
          background: GRADIENT_NAVY,
          borderRight: '1px solid rgba(255,255,255,.08)',
          boxShadow: '4px 0 28px rgba(15,31,61,.16)',
          transition: 'width .22s cubic-bezier(.4,0,.2,1)',
          overflow: 'hidden',
        }}
      >
          {/* facility */}
          <Box sx={{ px: collapsed ? 1.25 : 2, py: 2, borderBottom: '1px solid rgba(255,255,255,.11)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: collapsed ? 'center' : 'flex-start' }}>
              <Tooltip title={collapsed ? (facility?.name || 'Your facility') : ''} placement="right" arrow>
                <Box
                  sx={{
                    width: 34, height: 34, borderRadius: '9px', flexShrink: 0,
                    display: 'grid', placeItems: 'center', overflow: 'hidden',
                    background: facility?.logo ? '#fff' : 'rgba(255,255,255,.12)',
                    p: facility?.logo ? .5 : 0,
                  }}
                >
                  {facility?.logo
                    ? <Box component="img" src={facility.logo} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    : <Hospital sx={{ fontSize: 18, color: '#fff' }} />}
                </Box>
              </Tooltip>
              {!collapsed && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#fff', lineHeight: 1.3 }} noWrap>
                    {facility?.name || 'Your facility'}
                  </Typography>
                  <Typography sx={{ fontSize: 9.5, color: 'rgba(255,255,255,.45)', letterSpacing: '.7px', textTransform: 'uppercase' }}>
                    {PRODUCT.name}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* signed-in user */}
          <Box sx={{ px: collapsed ? 1.25 : 2, py: 1.75, borderBottom: '1px solid rgba(255,255,255,.11)', display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <Tooltip title={collapsed ? `${userInfo.name} · ${userInfo.role}` : ''} placement="right" arrow>
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: `linear-gradient(135deg, ${BRAND.greenLight}, ${BRAND.green})`,
                  display: 'grid', placeItems: 'center',
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>
                  {firstName.charAt(0).toUpperCase()}
                </Typography>
              </Box>
            </Tooltip>
            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#fff', lineHeight: 1.3 }} noWrap>
                  {userInfo.name}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: 'rgba(255,255,255,.5)' }} noWrap>
                  {userInfo.role}
                </Typography>
              </Box>
            )}
          </Box>

          {/* categories */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: collapsed ? 1 : 1.25, py: 1.5 }}>
            {!collapsed && (
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: 'rgba(255,255,255,.4)', letterSpacing: '1.3px', textTransform: 'uppercase', px: 1.25, mb: 1 }}>
                Categories
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: .375 }}>
              {railItems.map((item) => (
                <RailRow
                  key={item.key}
                  icon={item.icon} label={item.label} count={item.count} accent={item.accent}
                  collapsed={collapsed}
                  active={activeCat === item.key}
                  onClick={() => { setActiveCat(item.key); setSearchQuery(''); }}
                />
              ))}
            </Box>
          </Box>

          {/* quick action */}
          {userInfo.is_staff === 1 && (
            <Box sx={{ px: 1.75, py: 1.75, borderTop: '1px solid rgba(255,255,255,.11)' }}>
              <Tooltip
                title={collapsed
                  ? 'Request consumables'
                  : 'Send a request to Procurement for cotton, plaster, cannulas, etc.'}
                placement={collapsed ? 'right' : 'top'} arrow
              >
                <Button
                  fullWidth disableElevation
                  startIcon={collapsed ? null : <ShoppingCartIcon sx={{ fontSize: 17 }} />}
                  onClick={() => setConsumablesDialogOpen(true)}
                  sx={{
                    textTransform: 'none', fontWeight: 700, fontSize: 12.5,
                    py: 1, minWidth: 0, borderRadius: '10px', color: '#fff',
                    background: `linear-gradient(135deg, ${BRAND.greenLight} 0%, ${BRAND.green} 100%)`,
                    boxShadow: `0 4px 14px ${BRAND.green}40`,
                    '&:hover': { filter: 'brightness(1.07)' },
                    '& .MuiButton-startIcon': { mr: collapsed ? 0 : undefined },
                  }}
                >
                  {collapsed ? <ShoppingCartIcon sx={{ fontSize: 18 }} /> : 'Request Consumables'}
                </Button>
              </Tooltip>
            </Box>
          )}

          {/* ── Account actions ──
              Change password and Sign out live here as well as in the app-bar
              avatar menu. The rail is where someone already is when they think
              "I should change my password", and a menu hidden behind an
              unlabelled avatar is not discoverable. */}
          <Box sx={{ px: collapsed ? 1 : 1.25, py: 1.25, borderTop: '1px solid rgba(255,255,255,.11)' }}>
            {!collapsed && (
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: 'rgba(255,255,255,.4)', letterSpacing: '1.3px', textTransform: 'uppercase', px: 1.25, mb: .875 }}>
                Account
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: .375 }}>
              {[
                { key: 'pwd',  label: 'Change password', Icon: LockResetIcon, danger: false, onClick: () => setChangePasswordOpen(true) },
                { key: 'out',  label: 'Sign out',        Icon: SignOutIcon,   danger: true,  onClick: handleSignOut },
              ].map(({ key, label, Icon, danger, onClick }) => {
                const btn = (
                  <Box
                    key={key}
                    component="button" type="button" onClick={onClick}
                    aria-label={label}
                    sx={{
                      display: 'flex', alignItems: 'center', width: '100%',
                      gap: collapsed ? 0 : 1.25,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      px: collapsed ? .5 : 1.25, py: 1,
                      borderRadius: '10px', cursor: 'pointer', font: 'inherit', textAlign: 'left',
                      background: 'transparent', border: '1px solid transparent',
                      transition: 'background .16s ease, color .16s ease',
                      '&:hover': {
                        background: danger ? `${BRAND.danger}26` : 'rgba(255,255,255,.09)',
                        borderColor: danger ? `${BRAND.danger}59` : 'transparent',
                      },
                      '&:focus-visible': { outline: `2px solid ${BRAND.greenLight}`, outlineOffset: 2 },
                    }}
                  >
                    <Icon sx={{ fontSize: 18, flexShrink: 0, color: danger ? '#FF9B9B' : 'rgba(255,255,255,.6)' }} />
                    {!collapsed && (
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: danger ? '#FFC9C9' : 'rgba(255,255,255,.75)' }} noWrap>
                        {label}
                      </Typography>
                    )}
                  </Box>
                );
                return collapsed
                  ? <Tooltip key={key} title={label} placement="right" arrow>{btn}</Tooltip>
                  : btn;
              })}
            </Box>
          </Box>

          {/* collapse toggle + wordmark */}
          <Box
            sx={{
              px: collapsed ? 1 : 1.75, py: 1.25,
              borderTop: '1px solid rgba(255,255,255,.11)',
              display: 'flex', alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'space-between', gap: 1,
            }}
          >
            {!collapsed && (
              <Typography sx={{ fontSize: 9.5, color: 'rgba(255,255,255,.3)', letterSpacing: '.6px' }} noWrap>
                {PRODUCT.vendor} · {PRODUCT.name}
              </Typography>
            )}
            <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right" arrow>
              <IconButton
                size="small" onClick={toggleRail}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                sx={{
                  color: 'rgba(255,255,255,.6)', flexShrink: 0,
                  background: 'rgba(255,255,255,.07)',
                  '&:hover': { background: 'rgba(255,255,255,.16)', color: '#fff' },
                }}
              >
                {collapsed
                  ? <ChevronRight sx={{ fontSize: 18 }} />
                  : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

      {/* ══════════════════════════════════════════════════════════════════
          WORKING AREA
          Cleared past the rail by its FULL width, and carrying the page
          gutters itself because <main> has none (see the note on RAIL_WIDTH).
         ══════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          minWidth: 0,
          ml: { xs: 0, md: `${railW}px` },
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 2.5, sm: 3 },
          transition: 'margin-left .22s cubic-bezier(.4,0,.2,1)',
        }}
      >

          {/* greeting */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .875, mb: .75 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: BRAND.green }} />
              <Typography sx={{ fontSize: 10, fontWeight: 800, color: BRAND.green, letterSpacing: '1.4px', textTransform: 'uppercase' }}>
                {greetingFor(new Date())}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, color: BRAND.ink, letterSpacing: '-.8px', lineHeight: 1.15 }}>
              Welcome back, {firstName}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: BRAND.inkMuted, mt: .75 }}>
              {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              {' · '}Choose a department to begin your work.
            </Typography>
          </Box>

          {/* stats */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0,1fr))',
                md: 'repeat(4, minmax(0,1fr))',
              },
              gap: { xs: 1.25, sm: 1.75 }, mb: { xs: 2, sm: 2.5 },
            }}
          >
            <StatTile label="Departments" value={accessibleDepartments.length} accent={BRAND.navy} />
            <StatTile label="Categories"  value={Object.keys(railCounts).length} accent={BRAND.navy} />
            <StatTile label="Your role"   value={userInfo.role || '—'} small />
            <StatTile label="Status"      value="Online" accent={BRAND.green} small />
          </Box>

          {/* search */}
          <TextField
            fullWidth size="medium" placeholder="Search departments…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '13px', background: '#fff',
                '& fieldset':             { borderColor: BRAND.line },
                '&:hover fieldset':       { borderColor: BRAND.navy },
                '&.Mui-focused fieldset': { borderColor: BRAND.navy, borderWidth: '1.5px' },
                '&.Mui-focused':          { boxShadow: `0 0 0 4px ${BRAND.navy}14` },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: BRAND.inkFaint }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Clear search">
                    <ClearIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* mobile category strip — the rail's stand-in below md */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' }, gap: 1, mb: 2.5,
              overflowX: 'auto', pb: .75,
              '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
            }}
          >
            {railItems.map((item) => {
              const on = activeCat === item.key;
              return (
                <Box
                  key={item.key}
                  component="button" type="button"
                  onClick={() => { setActiveCat(item.key); setSearchQuery(''); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: .75, flexShrink: 0,
                    px: 1.5, py: .875, borderRadius: '20px', cursor: 'pointer', font: 'inherit',
                    background: on ? BRAND.navy : '#fff',
                    border: `1px solid ${on ? BRAND.navy : BRAND.line}`,
                    color: on ? '#fff' : BRAND.inkMuted,
                  }}
                >
                  <item.icon sx={{ fontSize: 15 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {item.label} · {item.count}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* result summary while searching */}
          {searching && (
            <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, mb: 2 }}>
              {visibleCount === 0
                ? <>No department matches <strong>“{searchQuery}”</strong>.</>
                : <>{visibleCount} department{visibleCount === 1 ? '' : 's'} matching <strong>“{searchQuery}”</strong> across all categories.</>}
            </Typography>
          )}

          {/* department groups */}
          {Object.entries(visibleGroups).map(([category, categoryDepartments]) => {
            const { icon: CatIcon, accent } = styleFor(category);
            return (
              <Box key={category} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.75 }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: '10px', background: `${accent}17`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <CatIcon sx={{ fontSize: 18, color: accent }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: BRAND.ink, lineHeight: 1.25 }}>
                      {category}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: BRAND.inkFaint }}>
                      {categoryDepartments.length} department{categoryDepartments.length === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, height: 1, background: BRAND.line, ml: 1 }} />
                </Box>

                {/* auto-fill, so cards are always equal width and the row fills
                    the space — the old <Grid item> produced ragged widths.
                    One column on a phone: two 150px cards side by side would
                    wrap "Wards & Nursing Services" onto four lines. */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(auto-fill, minmax(200px, 1fr))',
                      md: 'repeat(auto-fill, minmax(238px, 1fr))',
                    },
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  {categoryDepartments.map((department) => (
                    <Box
                      key={department.id}
                      component="button" type="button"
                      onClick={() => onDepartmentSelect(department)}
                      sx={{
                        textAlign: 'left', cursor: 'pointer', font: 'inherit', p: 0,
                        borderRadius: '14px', overflow: 'hidden', minWidth: 0,
                        background: '#fff',
                        border: `1px solid ${BRAND.line}`,
                        transition: 'transform .16s ease, box-shadow .16s ease, border-color .16s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          borderColor: accent,
                          boxShadow: `0 10px 26px ${accent}26`,
                        },
                        '&:hover .dept-arrow': { background: accent, color: '#fff', transform: 'translateX(2px)' },
                        '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
                      }}
                    >
                      <Box sx={{ height: 3, background: accent }} />
                      <Box sx={{ p: 2.25 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: `${accent}17`, display: 'grid', placeItems: 'center' }}>
                            <CatIcon sx={{ fontSize: 19, color: accent }} />
                          </Box>
                          <Box
                            className="dept-arrow"
                            sx={{
                              width: 26, height: 26, borderRadius: '8px',
                              background: BRAND.canvas, color: BRAND.inkFaint,
                              display: 'grid', placeItems: 'center',
                              transition: 'all .16s ease',
                            }}
                          >
                            <ChevronRight sx={{ fontSize: 17 }} />
                          </Box>
                        </Box>

                        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: BRAND.ink, lineHeight: 1.35, mb: .625 }}>
                          {department.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12.5, color: BRAND.inkMuted, lineHeight: 1.65,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            overflow: 'hidden', minHeight: '2.6em',
                          }}
                        >
                          {department.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}

          {/* empty */}
          {visibleCount === 0 && (
            <Box
              sx={{
                textAlign: 'center', py: 7, px: 3,
                background: '#fff', borderRadius: '16px', border: `1px dashed ${BRAND.line}`,
              }}
            >
              <Box sx={{ width: 58, height: 58, borderRadius: '50%', mx: 'auto', mb: 2, display: 'grid', placeItems: 'center', background: BRAND.canvas }}>
                {searching
                  ? <SearchIcon sx={{ fontSize: 27, color: BRAND.inkFaint }} />
                  : <PersonIcon sx={{ fontSize: 27, color: BRAND.inkFaint }} />}
              </Box>
              <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: BRAND.ink, mb: .875 }}>
                {searching ? 'No matching departments' : 'No departments assigned'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, lineHeight: 1.75, maxWidth: 400, mx: 'auto' }}>
                {searching
                  ? 'Try a different word, or clear the search to see everything you have access to.'
                  : 'Your role does not have access to any department yet. Ask a System Administrator to grant access.'}
              </Typography>
              {searching && (
                <Button
                  onClick={() => setSearchQuery('')}
                  sx={{ mt: 2.5, textTransform: 'none', fontWeight: 700, color: BRAND.navy }}
                >
                  Clear search
                </Button>
              )}
            </Box>
          )}
      </Box>

      {/* Consumables requisition — in-charge users only */}
      <ConsumableRequisitionDialog
        open={consumablesDialogOpen}
        onClose={() => setConsumablesDialogOpen(false)}
      />

      <ChangePasswordDialog
        open={changePasswordOpen}
        userInfo={userInfo}
        onClose={() => setChangePasswordOpen(false)}
      />
    </Box>
  );
};

export default Dashboard;
